import { useState, useEffect } from "react";
import { Stack, DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, Spinner, SpinnerSize, DefaultButton, Text, Dropdown, IDropdownOption, PrimaryButton } from "@fluentui/react";
import { getSuCosts, getSuCompanies, downloadSuCosts } from "../../api/superuser/api_superuser";
import { CostsResponse, CostLog, ServiceCost } from "../../api/admin/models_admin";
import { SuCostsResponse, SuCompanyInfo } from "../../api/superuser/models_superuser";
import { AdminLabel, CellContent, ErrorDialog } from "../admin/components";
import { ApiCostList, mergeApiCosts } from "../admin/costs/apiCosts";


const aggregateCostsData = (allCostsData: SuCostsResponse[], year: number, month: number): CostsResponse => {
    // 全会社のloglistを集計
    const aggregatedLogs: { [key: string]: CostLog } = {};
    allCostsData.forEach(companyData => {
        companyData.data?.loglist?.forEach(log => {
            const key = `${log.model}_${log.type}`;
            if (aggregatedLogs[key]) {
                aggregatedLogs[key].total_token_count = 
                    (aggregatedLogs[key].total_token_count || 0) + (log.total_token_count || 0);
            } else {
                aggregatedLogs[key] = { ...log };
            }
        });
    });
    // 全会社のservice_costsを集計
    const aggregatedServices: { [key: string]: ServiceCost } = {};
    allCostsData.forEach(companyData => {
        companyData.data?.service_costs?.forEach(service => {
            const key = service.service_name ?? "";
            if (aggregatedServices[key]) {
                aggregatedServices[key].total_count = 
                    (aggregatedServices[key].total_count || 0) + (service.total_count || 0);
                aggregatedServices[key].total_cost = 
                    (aggregatedServices[key].total_cost || 0) + (service.total_cost || 0);
            } else {
                aggregatedServices[key] = { ...service };
            }
        });
    });
    // 合計金額を計算
    const totalCost = allCostsData.reduce((sum, item) => {
        const cost = Number(item.data.costs_total.replaceAll(",", ""));
        return sum + (isNaN(cost) ? 0 : cost);
    }, 0);
    return {
        target_year: year,
        target_month: month,
        costs_total: totalCost.toFixed(2),
        msg: '',
        loglist: Object.values(aggregatedLogs),
        service_costs: Object.values(aggregatedServices),
        api_costs: mergeApiCosts(allCostsData.map(companyData => companyData.data?.api_costs ?? []))
    };
}

export const SuperuserCosts = () => {
    const [allCostsData, setAllCostsData] = useState<SuCostsResponse[]>([]);
    const [companies, setCompanies] = useState<SuCompanyInfo[]>([]);
    const [selectedCompanyCd, setSelectedCompanyCd] = useState<number | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
    const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth() + 1);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    const allCompanyKey = Number.MIN_SAFE_INTEGER

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        try {
            const companiesData = await getSuCompanies();
            if ("error" in companiesData) {
                setError(companiesData.error);
            } else {
                setCompanies(companiesData);
            }
        } catch (err) {
            setError("会社情報の取得中にエラーが発生しました");
        }
    };

    const fetchCosts = async (year: number, month: number, companyCd: number | undefined) => {
        setLoading(true);
        setError(null);
        try {
            const data = await getSuCosts(year, month, companyCd === allCompanyKey ? undefined : companyCd);
            if ("error" in data) {
                setError(data.error);
            } else {
                setAllCostsData(data);
                setCurrentYear(year);
                setCurrentMonth(month);
            }
        } catch (err) {
            setError("コスト情報の取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handlePrevMonth = () => {
        let newYear = currentYear;
        let newMonth = currentMonth - 1;
        if (newMonth === 0) {
            newYear -= 1;
            newMonth = 12;
        }
        fetchCosts(newYear, newMonth, selectedCompanyCd);
    };

    const handleCurrentMonth = () => {
        const now = new Date();
        fetchCosts(now.getFullYear(), now.getMonth() + 1, selectedCompanyCd);
    };

    const handleNextMonth = () => {
        let newYear = currentYear;
        let newMonth = currentMonth + 1;
        if (newMonth === 13) {
            newYear += 1;
            newMonth = 1;
        }
        fetchCosts(newYear, newMonth, selectedCompanyCd);
    };

    const handleCompanyChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        const company_cd = Number(option?.key)
        setSelectedCompanyCd(company_cd);
        fetchCosts(currentYear, currentMonth, company_cd);
    };

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const result = await downloadSuCosts(currentYear, currentMonth);
            if (result && "error" in result) {
                setError(result.error);
            }
        } catch (err) {
            setError("ダウンロード中にエラーが発生しました");
        } finally {
            setIsDownloading(false);
        }
    };

    const companyOptions: IDropdownOption[] = [
        { key: allCompanyKey, text: '全会社' },
        ...companies.map(company => ({
            key: company.company_cd,
            text: company.company_name
        }))
    ];

    const currentCostsData = aggregateCostsData(allCostsData, currentYear, currentMonth);
    
    const formatNumber = (num: number | null | undefined): string => {
        if (num === null || num === undefined) return '0';
        return num.toLocaleString('ja-JP');
    };

    const formatCost = (num: number | null | undefined): string => {
        if (num === null || num === undefined) return '不明';
        return num.toFixed(3);
    };

    const calculateTotal = (tokenCount: number | null, cost: number | null): string => {
        if (tokenCount === null || tokenCount === undefined || cost === null || cost === undefined) {
            return '不明';
        }
        return (tokenCount * cost).toFixed(2);
    };

    const isInitialState = allCostsData.length === 0 && selectedCompanyCd === undefined
    const isSelectingAllCompany = allCostsData.length > 0 && selectedCompanyCd === undefined
    const currentSelectedCompanyName = isInitialState ? undefined : 
        (isSelectingAllCompany ? "全会社" : (companies.find((v) => v.company_cd == selectedCompanyCd)?.company_name ?? ""));

    const costLogColumns: IColumn[] = [
        {
            key: 'model',
            name: 'モデル名',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true,
            onRender: (log: CostLog) => (
                <CellContent>{log.model || '不明'}</CellContent>
            )
        },
        {
            key: 'type',
            name: '入出力',
            minWidth: 80,
            maxWidth: 100,
            isResizable: true,
            onRender: (log: CostLog) => (
                <CellContent>{log.type === 1 ? '入力' : '出力'}</CellContent>
            )
        },
        {
            key: 'total_token_count',
            name: 'トークン数',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            onRender: (log: CostLog) => (
                <div style={{ textAlign: 'right', fontSize: "11pt", fontWeight: 400, color: "black" }}>
                    {formatNumber(log.total_token_count)}
                </div>
            )
        },
        {
            key: 'unit_cost',
            name: '単価(円)\n(1,000 トークンあたり)',
            minWidth: 120,
            maxWidth: 180,
            isResizable: true,
            isMultiline: true,
            onRender: (log: CostLog) => (
                <div style={{ textAlign: 'right', fontSize: "11pt", fontWeight: 400, color: "black" }}>
                    {log.costs !== null && log.costs !== undefined ? formatCost(log.costs * 1000) : '不明'}
                </div>
            )
        },
        {
            key: 'total_cost',
            name: '金額(円)',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            onRender: (log: CostLog) => (
                <div style={{ textAlign: 'right', fontSize: "11pt", fontWeight: 400, color: "black" }}>
                    {calculateTotal(log.total_token_count, log.costs)}
                </div>
            )
        }
    ];

    const serviceCostColumns: IColumn[] = [
        {
            key: 'service_name',
            name: 'サービス名',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true,
            onRender: (service: ServiceCost) => (
                <CellContent>{service.service_name || '不明'}</CellContent>
            )
        },
        {
            key: 'total_count',
            name: 'カウント',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            onRender: (service: ServiceCost) => (
                <div style={{ textAlign: 'right', fontSize: "11pt", fontWeight: 400, color: "black" }}>
                    {service.total_count !== null && service.total_count !== undefined 
                        ? formatNumber(service.total_count) 
                        : ''}
                </div>
            )
        },
        {
            key: 'unit_name',
            name: '単位',
            minWidth: 80,
            maxWidth: 120,
            isResizable: true,
            onRender: (service: ServiceCost) => (
                <CellContent>{service.unit_name || ''}</CellContent>
            )
        },
        {
            key: 'total_cost',
            name: '金額(円)',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            onRender: (service: ServiceCost) => (
                <div style={{ textAlign: 'right', fontSize: "11pt", fontWeight: 400, color: "black" }}>
                    {service.total_cost !== null && service.total_cost !== undefined 
                        ? formatCost(service.total_cost) 
                        : '不明'}
                </div>
            )
        }
    ];

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="従量課金照会" />
            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
                <Dropdown
                    placeholder="会社を選択"
                    label="会社選択"
                    options={companyOptions}
                    selectedKey={selectedCompanyCd}
                    onChange={handleCompanyChange}
                    styles={{ dropdown: { width: 300 } }}
                    disabled={isDownloading}
                />
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <DefaultButton text="前の月" onClick={handlePrevMonth} disabled={isDownloading} />
                    <DefaultButton text="今月" onClick={handleCurrentMonth} disabled={isDownloading} />
                    <DefaultButton text="次の月" onClick={handleNextMonth} disabled={isDownloading} />
                </Stack>
                <Stack.Item grow>
                    <Stack horizontal horizontalAlign="end">
                        <PrimaryButton text={`全社分${currentYear}年${currentMonth}月利用分請求情報`} onClick={handleDownload} iconProps={{ iconName: 'Download' }} disabled={isDownloading} />
                    </Stack>
                </Stack.Item>
            </Stack>
                <Text variant="xLarge" block>
                    {currentCostsData?.target_year}年{currentCostsData?.target_month}月分 {currentSelectedCompanyName ? `＜${currentSelectedCompanyName}＞` : ""} :  {currentCostsData?.costs_total} 円 {currentCostsData?.msg}
                </Text>

                <Stack tokens={{ childrenGap: 20 }}>
                    <DetailsList
                        items={currentCostsData?.loglist || []}
                        columns={costLogColumns}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={SelectionMode.none}
                        isHeaderVisible={true}
                    />
                    <DetailsList
                        items={currentCostsData?.service_costs || []}
                        columns={serviceCostColumns}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={SelectionMode.none}
                        isHeaderVisible={true}
                    />
                    <ApiCostList apiCosts={currentCostsData?.api_costs} />
                </Stack>
        </Stack>
    );
}